import React from "react";

import { Autocomplete, CircularProgress, TextField } from "@mui/material";

export interface IOption {
  id?: string;
  value: string;
  description: string;
}

interface IProps {
  options: IOption[];
  asyncSearch: (filter?: string) => Promise<void>;
  setOptions: (options: IOption[]) => void;
  setOption: (option: IOption | null) => void;
  setInputValue: (value: string) => void;
  label?: string;
  loading: boolean;
  option: IOption | null;
  error?: boolean;
  errorMessage?: string | null;
  disabled?: boolean;
  required?: boolean;
  autofocus?: boolean;
}

const AutoComplete = ({
  options,
  loading,
  option,
  error,
  errorMessage,
  label,
  asyncSearch,
  setOption,
  setOptions,
  setInputValue,
  disabled,
  required = false,
  autofocus = false,
}: IProps) => {
  const [open, setOpen] = React.useState(false);

  const loadingInput = open;

  React.useEffect(() => {
    let active = true;

    if (!loadingInput) {
      return undefined;
    }

    (async () => {
      if (active) {
        await asyncSearch("");
      }
    })();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingInput]);

  React.useEffect(() => {
    if (!open) {
      setOptions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <div>
      {
        <Autocomplete
          freeSolo
          autoFocus={autofocus}
          disabled={disabled}
          open={open}
          value={option}
          onChange={(e: any, newValue) => {
            if (typeof newValue === "string") {
              setOption(null);
              setInputValue(newValue);
            } else {
              setOption(newValue);
            }
          }}
          onInputChange={(e, newInputValue) => {
            setInputValue(newInputValue);
            asyncSearch(newInputValue);
          }}
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          filterOptions={x => x}
          isOptionEqualToValue={(option, value) => {
            if (option?.id && value?.id) return option.id === value.id;
            return option?.value === value?.value;
          }}
          getOptionLabel={option => {
            if (typeof option === "string") return option;
            return option?.value || "";
          }}
          renderOption={(props, option) => (
            <li {...props} key={option.value}>
              {option.description ? `${option.value}` : option.value}
            </li>
          )}
          options={options}
          loading={loading}
          slotProps={{
            paper: {
              sx: {
                width: "100% !important",
                minWidth: 0,
                boxSizing: "border-box",
              },
            },
          }}
          renderInput={params => (
            <TextField
              {...params}
              label={label || "Pesquisar"}
              error={error || false}
              helperText={errorMessage || ""}
              size="small"
              required={required}
              autoFocus={autofocus}
              InputProps={{
                ...params?.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params?.InputProps?.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
        />
      }
    </div>
  );
};

export default AutoComplete;
